import React, { useState, useEffect } from 'react';
import { X, User, Save, Globe } from 'lucide-react';
import { updateProfile } from 'firebase/auth';
import { doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { SUPPORTED_CURRENCIES, detectUserCurrency } from '../services/currencyService';
import toast from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';

const ProfileModal = ({ isOpen, onClose, user }) => {
  const { t } = useTranslation();
  // Parse displayName into firstName and lastName
  const parseDisplayName = (displayName) => {
    if (!displayName) return { firstName: '', lastName: '' };
    const parts = displayName.trim().split(' ');
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || ''
    };
  };

  const { firstName: initialFirstName, lastName: initialLastName } = parseDisplayName(user?.displayName);

  const [formData, setFormData] = useState({
    firstName: initialFirstName,
    lastName: initialLastName,
    email: user?.email || '',
    currency: user?.preferences?.currency || 'NOK'
  });
  const [loading, setLoading] = useState(false);
  const [detectingCurrency, setDetectingCurrency] = useState(false);

  // Update form data when user prop changes
  useEffect(() => {
    if (user) {
      const { firstName, lastName } = parseDisplayName(user.displayName);
      setFormData({
        firstName,
        lastName,
        email: user.email || '',
        currency: user.preferences?.currency || 'NOK'
      });
    }
  }, [user]);

  // Detect user's currency when modal opens and user doesn't have a currency preference
  useEffect(() => {
    if (isOpen && user && !user.preferences?.currency) {
      const detectCurrency = async () => {
        setDetectingCurrency(true);
        try {
          const detectedCurrency = await detectUserCurrency();
          setFormData(prev => ({
            ...prev,
            currency: detectedCurrency.code
          }));
        } catch (error) {
          console.error('Failed to detect currency:', error);
        }
        setDetectingCurrency(false);
      };

      detectCurrency();
    }
  }, [isOpen, user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName.trim()) {
      toast.error(t('profileModal.firstNameRequired'));
      return;
    }

    setLoading(true);

    try {
      // Combine firstName and lastName for displayName
      const displayName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
      
      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, {
        displayName: displayName
      });

      // Check if user document exists, create if it doesn't
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // Create user document if it doesn't exist
        await setDoc(userDocRef, {
          uid: user.uid,
          email: user.email,
          displayName: displayName,
          createdAt: new Date(),
          preferences: {
            currency: formData.currency,
            location: null
          }
        });
        console.log('Created missing user document');
      } else {
        // Update existing user document
        await updateDoc(userDocRef, {
          displayName: displayName,
          'preferences.currency': formData.currency
        });
      }

      toast.success(t('profileModal.profileUpdated'));
      onClose();
    } catch (error) {
      toast.error(t('profileModal.updateFailed'));
      console.error('Error updating profile:', error);
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <User className="h-5 w-5 mr-2 text-primary-600" />
            {t('profileModal.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                {t('profileModal.firstName')} *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                required
                className="input-field"
                placeholder={t('profileModal.firstNamePlaceholder')}
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                {t('profileModal.lastName')} *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                required
                className="input-field"
                placeholder={t('profileModal.lastNamePlaceholder')}
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {t('profileModal.nameHelp')}
          </p>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('profileModal.email')}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              disabled
              className="input-field bg-gray-100"
              value={formData.email}
            />
            <p className="mt-1 text-sm text-gray-500">
              {t('profileModal.emailHelp')}
            </p>
          </div>

          <div>
            <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
              <Globe className="h-4 w-4 inline mr-1" />
              {t('profileModal.currency')}
            </label>
            <select
              id="currency"
              name="currency"
              className="input-field"
              value={formData.currency}
              onChange={handleChange}
              disabled={detectingCurrency}
            >
              {SUPPORTED_CURRENCIES.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.symbol} {currency.name} ({currency.code})
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {detectingCurrency ? t('profileModal.detectingCurrency') : t('profileModal.currencyHelp')}
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center space-x-2"
              disabled={loading}
            >
              <Save className="h-4 w-4" />
              <span>{loading ? t('profileModal.saving') : t('profileModal.saveChanges')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileModal;
